import { Loader2, Search } from 'lucide-react'
import type { SearchFacets, SearchFilters } from '@agentwiki/shared'
import type { SearchSource, SearchType } from '../../hooks/use-search'
import { cn } from '../../lib/utils'
import { SearchFiltersBar } from './search-filters'
import { SegmentedControl } from './search-workspace-parts'
import { getSourceOptions, TYPE_OPTIONS } from './search-workspace-state'

interface SearchWorkspaceControlsProps {
  isDark: boolean
  isFetching: boolean
  input: string
  source: SearchSource
  type: SearchType
  supportsFilters: boolean
  facets?: SearchFacets
  filters: SearchFilters
  onInputChange: (value: string) => void
  onSourceChange: (source: SearchSource) => void
  onTypeChange: (type: SearchType) => void
  onFiltersChange: (filters: SearchFilters) => void
}

export function SearchWorkspaceControls({
  isDark,
  isFetching,
  input,
  source,
  type,
  supportsFilters,
  facets,
  filters,
  onInputChange,
  onSourceChange,
  onTypeChange,
  onFiltersChange,
}: SearchWorkspaceControlsProps) {
  return (
    <section
      aria-labelledby="search-controls-title"
      className={cn(
        'rounded-lg border p-4',
        isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white',
      )}
    >
      <h2 id="search-controls-title" className="sr-only">
        Search knowledge
      </h2>
      <div className="relative">
        {isFetching ? (
          <Loader2
            className={cn(
              'absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin',
              isDark ? 'text-brand-400' : 'text-brand-500',
            )}
            aria-hidden="true"
          />
        ) : (
          <Search
            className={cn(
              'absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2',
              isDark ? 'text-neutral-500' : 'text-neutral-400',
            )}
            aria-hidden="true"
          />
        )}
        <input
          aria-label="Search workspace"
          value={input}
          onChange={(event) => onInputChange(event.target.value)}
          placeholder="Search documents, files, handoffs, decisions..."
          className={cn(
            'aw-search-control h-11 w-full rounded-lg border bg-transparent pl-10 pr-3 text-sm outline-none',
            isDark
              ? 'border-white/[0.08] text-neutral-100 placeholder-neutral-400 focus:border-brand-500/50'
              : 'border-neutral-200 text-neutral-900 placeholder-neutral-400 focus:border-brand-500',
          )}
          autoFocus
        />
      </div>

      <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <SegmentedControl
          label="Source"
          value={source}
          options={getSourceOptions()}
          isDark={isDark}
          onChange={onSourceChange}
        />
        <SegmentedControl
          label="Type"
          value={type}
          options={TYPE_OPTIONS}
          isDark={isDark}
          onChange={onTypeChange}
        />
      </div>

      {supportsFilters && facets && (
        <div
          className={cn(
            'mt-4 border-t pt-3',
            isDark ? 'border-white/[0.06]' : 'border-neutral-100',
          )}
        >
          <SearchFiltersBar
            filters={filters}
            facets={facets}
            onChange={onFiltersChange}
            isDark={isDark}
          />
        </div>
      )}
      {!supportsFilters && (
        <p
          className={cn(
            'mt-4 border-t pt-3 text-xs',
            isDark ? 'border-white/[0.06] text-neutral-400' : 'border-neutral-100 text-neutral-600',
          )}
        >
          Document filters are unavailable for File results. Choose Documents to filter by category,
          tag, or date.
        </p>
      )}
    </section>
  )
}
